import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';
import { LEVELS } from '../../constants/levels.js';
import { Btn } from '../ui/Btn.jsx';
import { PB } from '../ui/ProgressBar.jsx';
import { LB } from '../ui/LevelBadge.jsx';
import { FinleyInsightBanner } from '../ui/FinleyInsightBanner.jsx';
import { LoadingDots } from '../ui/LoadingDots.jsx';
import { getInsightMessage } from '../../config/insightMessages.js';
import { getPhonics } from '../../lib/content-fetch.js';
import { getSpellingWords } from '../../lib/spelling.js';
import { getDueWords, prioritizeWithLeitner } from '../../lib/leitner.js';
import { rqConfettiBurst } from '../../lib/animations.js';
import { useTTS } from '../../hooks/useTTS.js';
import { shuffle } from '../../lib/utils.js';
import { popIn, slideUp, tapBounce } from '../../lib/motionPresets.js';

export function PhonicsGame({theme, tracker, profile, onComplete, onRecord, online, sessionActivityIndex = 0, isPlaylistSession = false}) {
  const [words, setWords] = useState(null);
  const [idx, setIdx] = useState(0);
  const [rev, setRev] = useState([]);
  const [show, setShow] = useState(false);
  const [stars, setStars] = useState(0);
  const [done, setDone] = useState(false);
  const [insightMessage, setInsightMessage] = useState('');
  const [insightVisible, setInsightVisible] = useState(false);
  const [knowItReady, setKnowItReady] = useState(false);
  const [dueSet, setDueSet] = useState(new Set());
  const wStart = useRef(Date.now());
  const prevBktRef = useRef(tracker.bkt || {});
  const { speak, stop } = useTTS();
  const lv = tracker.currentLevel;
  const ml = LEVELS[lv]?.ml?.p || "Sound It Out";
  const chillMsg = tracker.chillMode ? "You're doing great! Let's review together 🌟" : null;
  const phonicsCount = tracker.chillMode ? 3 : 5;

  useEffect(() => {
    getPhonics(lv, theme.id, profile, online).then(w => {
      const spArr = getSpellingWords(profile?.id).map(sw => ({
        word: sw.word,
        sounds: sw.phonemes ? sw.phonemes.split('-').filter(Boolean) : [sw.word],
        sentence: sw.sentence,
        isSpelling: true
      }));
      const existSet = new Set(w.map(x => x.word));
      const newSp = spArr.filter(s => !existSet.has(s.word)).slice(0, 2);
      const combined = [...newSp, ...w];
      const prioritized = prioritizeWithLeitner(combined, tracker.wordMastery, profile.age);
      const dueWords = getDueWords(tracker.wordMastery, profile.age || 7, 20);
      setDueSet(new Set(dueWords));
      setWords(shuffle(prioritized).slice(0, phonicsCount));
    });
  }, []);

  useEffect(() => {
    if (!words || words.length === 0) return;
    const currentWord = words[idx]?.word;
    const triggers = [];
    if (dueSet.has(currentWord)) triggers.push('leitner_review');
    if (idx > 0 && idx % 3 === 0) triggers.push('periodic');
    const msg = getInsightMessage({
      activityKey: `phonics:${sessionActivityIndex}:${idx}:${currentWord}`,
      sessionStart: isPlaylistSession && sessionActivityIndex === 0 && idx === 0,
      triggers
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [words, idx, dueSet, sessionActivityIndex, isPlaylistSession]);

  useEffect(() => {
    const prev = prevBktRef.current || {};
    const next = tracker.bkt || {};
    const crossed = Object.keys(next).some((skill) => {
      const p = prev[skill] || 0;
      const n = next[skill] || 0;
      return (p < 0.6 && n >= 0.6) || (p < 0.85 && n >= 0.85);
    });
    prevBktRef.current = next;
    if (!crossed) return;
    const msg = getInsightMessage({
      activityKey: `phonics:milestone:${sessionActivityIndex}:${idx}`,
      triggers: ['skill_milestone']
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [tracker.bkt, idx, sessionActivityIndex]);

  useEffect(() => {
    const currentWord = words?.[idx]?.word;
    if (!currentWord) return;
    speak(currentWord, { rate: 0.95, pitch: 1.0 });
  }, [words, idx, speak]);

  useEffect(() => () => stop(), [stop]);

  if (!words || words.length === 0) return <LoadingDots msg="Loading sounds..."/>;
  const word = words[idx];

  if (done) return (
    <div style={{textAlign:"center",padding:"36px 24px"}}>
      <div style={{fontSize:64,marginBottom:10,animation:"float 2s ease-in-out infinite"}}>🎉</div>
      <h2 style={{fontFamily:F,fontSize:28,color:C.text}}>Amazing!</h2>
      <LB level={lv}/>
      <div style={{display:"flex",gap:3,justifyContent:"center",margin:"16px 0"}}>
        {Array.from({length:stars}).map((_,i) => <span key={i} style={{fontSize:30,animation:`popIn 0.3s ${i*.08}s both`,display:"inline-block"}}>⭐</span>)}
      </div>
      <Btn onClick={() => onComplete(stars)}>Keep Going →</Btn>
    </div>
  );

  const allR = rev.length === word.sounds.length;
  const revealBubble = (i) => {
    if (!allR && !rev.includes(i)) {
      const n = [...rev, i];
      setRev(n);
      if (n.length === word.sounds.length) setTimeout(() => { setShow(true); setTimeout(() => setKnowItReady(true), 1000); }, 300);
    }
  };
  const next = (known) => {
    const ms = Date.now() - wStart.current;
    onRecord("phonics", word.word, known, ms);
    if (known) { setStars(s => s + 1); rqConfettiBurst({count:20,spread:50}); }
    if (idx + 1 >= words.length) setDone(true);
    else { setIdx(idx + 1); setRev([]); setShow(false); setKnowItReady(false); wStart.current = Date.now(); }
  };

  return (
    <motion.div {...slideUp} className="relative text-center" style={{padding:"20px 24px"}}>
      <FinleyInsightBanner message={insightMessage} visible={insightVisible} />
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontFamily:N,fontSize:13,color:C.textLight}}>{idx+1}/{words.length}</span>
        <LB level={lv}/>
        <span style={{fontFamily:N,fontSize:13,color:C.primary,fontWeight:700}}>⭐ {stars}</span>
      </div>
      <PB current={idx} total={words.length} color={theme.color}/>
      <p style={{fontFamily:F,fontSize:16,color:theme.color,margin:"20px 0 4px"}}>{ml}</p>
      {chillMsg && <p style={{fontFamily:N,fontSize:13,color:C.success,margin:"0 0 8px"}}>{chillMsg}</p>}
      <p style={{fontFamily:N,fontSize:14,color:C.textMid,margin:"0 0 18px"}}>Tap each bubble!</p>
      <motion.div className="mb-5 flex flex-wrap justify-center gap-3" variants={{ animate: { transition: { staggerChildren: 0.08 } } }} animate="animate">
        {word.sounds.map((s,i) => (
          <motion.button key={i} {...tapBounce} initial={{ opacity: 0, scale: 0.7, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} onClick={!rev.includes(i) ? () => revealBubble(i) : undefined}
            style={{width:68,height:68,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:lv>=11?18:26,fontFamily:F,fontWeight:700,cursor:rev.includes(i)?"default":"pointer",transition:"all 0.3s",background:rev.includes(i)?theme.color:theme.colorLight,color:rev.includes(i)?"#fff":theme.color,border:rev.includes(i)?`3px solid ${theme.color}`:`3px dashed ${theme.color}66`,boxShadow:rev.includes(i)?`0 4px 16px ${theme.color}44`:"none",animation:rev.includes(i)?"popIn 0.3s both":"none"}}>
            {rev.includes(i) ? s : "?"}
          </motion.button>
        ))}
      </motion.div>
      {show && (
        <motion.div {...popIn}>
          <motion.div className="inline-block rounded-finley-lg shadow-finley-md" style={{padding:"12px 32px",background:C.accentLight,border:`3px solid ${C.accent}`,marginBottom:18}}>
            <span style={{fontFamily:F,fontSize:lv>=11?28:38,color:C.text,letterSpacing:lv>=11?2:4}}>{word.word}</span>
          </motion.div>
          {word.note && <p style={{fontFamily:N,fontSize:13,color:C.textMid,margin:"4px 0 4px"}}>{word.note}</p>}
          {word.sentence && <p style={{fontFamily:N,fontSize:14,color:C.text,margin:"4px 0 16px",fontStyle:"italic",lineHeight:1.5}}>"{word.sentence}"</p>}
          <br/>
          <div style={{display:"flex",gap:10,justifyContent:"center"}}>
            <Btn onClick={knowItReady ? () => next(true) : undefined} color={theme.color} disabled={!knowItReady}>I Know It! ⭐</Btn>
            <motion.button {...tapBounce} onClick={knowItReady ? () => next(false) : undefined} disabled={!knowItReady} className="min-h-[48px] rounded-finley-lg border-2 px-5 py-3 font-finley text-[15px] font-semibold shadow-finley-sm"
              style={{padding:"14px 20px",fontSize:15,fontFamily:N,fontWeight:600,background:knowItReady?"#F8F9FA":"#E5E7EB",border:"2px solid #E5E7EB",borderRadius:16,color:knowItReady?C.textMid:"#9CA3AF",cursor:knowItReady?"pointer":"default"}}>
              Still Learning
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
